import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import Swal from 'sweetalert2'
import {GET, POST, PUT} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate, useParams} from 'react-router-dom'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {HospitalityTabs, TabItem} from '../../../components/HospitalityTabs/HospitalityTabs'

import {ReservationRoomStatus, formatDateForInput} from './types'
import {
  ActivityReservation,
  TourReservation,
  ExperienceReservation,
  ReservationSummary,
  ReservationService,
} from '../../../../Types/HotelBookingTypes'
import {
  RoomOption,
  CustomerOption,
  FormData,
  HotelOrder,
  HotelOrdersResponse,
} from './types/tabTypes'
import {
  BasicInfoTab,
  SummaryTab,
  ActivitiesTab,
  ToursTab,
  ExperiencesTab,
  HotelOrdersTab,
  ServicesTab,
} from './components'

const EditHotelReservationContent: FC = () => {
  const navigate = useNavigate()
  const [, setOriginalStatus] = useState<ReservationRoomStatus>(ReservationRoomStatus.PENDING)
  const [activeTab, setActiveTab] = useState('basic') // Tab control
  const [atividades, setAtividades] = useState<ActivityReservation[]>([])
  const [tours, setTours] = useState<TourReservation[]>([])
  const [experiencias, setExperiencias] = useState<ExperienceReservation[]>([])
  const [reservationServices, setReservationServices] = useState<ReservationService[]>([])
  const [reservationSummary, setReservationSummary] = useState<ReservationSummary | null>(null)
  const [hotelOrders, setHotelOrders] = useState<HotelOrder[]>([])
  const [hotelOrdersTotal, setHotelOrdersTotal] = useState({totalSpent: 0, totalPay: 0})
  const [loadingOrders, setLoadingOrders] = useState(false)

  const {id} = useParams()
  const [loading, setLoading] = useState(false)
  const [rooms, setRooms] = useState<RoomOption[]>([])
  const [customers, setCustomers] = useState<CustomerOption[]>([])
  const [originalReservation, setOriginalReservation] = useState<any>(null)
  const [formData, setFormData] = useState<FormData>({
    roomId: '',
    customerId: '',
    checkIn: '',
    checkOut: '',
    status: ReservationRoomStatus.PENDING,
  })

  const handleInputChange = (field: string, value: string | ReservationRoomStatus) => {
    setFormData({
      ...formData,
      [field]: value,
    })
  }

  const validateForm = () => {
    if (!formData.roomId) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Selecione um quarto!',
      })
      return false
    }

    if (!formData.customerId) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Selecione um cliente!',
      })
      return false
    }

    if (!formData.checkIn) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Data de check-in é obrigatória!',
      })
      return false
    }

    //se for concluido deve possuir checkout
    if (formData.status === ReservationRoomStatus.COMPLETED && !formData.checkOut) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Para mudar o status para Finalizado deve possuir data de checkout',
      })
      return false
    }

    // Validar se check-out é depois do check-in
    if (formData.checkOut) {
      const checkInDate = new Date(formData.checkIn)
      const checkOutDate = new Date(formData.checkOut)
      if (checkOutDate <= checkInDate) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Data de check-out deve ser posterior ao check-in!',
        })
        return false
      }
    }

    return true
  }

  const getReservation = async () => {
    try {
      setLoading(true)
      const response = await GET(`/hotel-reservation/${id}`)

      if (response.success) {
        const reservation = response.dados[0]
        setOriginalReservation(reservation)
        setFormData({
          roomId: reservation.roomId.toString(),
          customerId: reservation.customerId.toString(),
          checkIn: formatDateForInput(reservation.checkIn),
          checkOut: reservation.checkOut ? formatDateForInput(reservation.checkOut) : '',
          status: reservation.status,
        })
        setOriginalStatus(reservation.status)
      } else {
        throw new Error(response.message || 'Erro ao carregar reserva')
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e.message || 'Erro ao carregar os dados da reserva!',
      }).then(() => {
        navigate('/hospitality/hotel-reservation')
      })
      console.log(e)
    }
  }

  const getRooms = async () => {
    try {
      // Buscar todos os quartos (disponíveis + o quarto atual da reserva)
      const res = await GET('/hotel-room')
      setRooms(res.data || [])
    } catch (e) {
      console.log('Erro ao carregar quartos:', e)
      setRooms([])
    }
  }

  const getCustomers = async () => {
    try {
      const res = await GET('/customer')
      setCustomers(res.data || [])
    } catch (e) {
      console.log('Erro ao carregar clientes:', e)
      setCustomers([])
    }
  }

  const updateReservation = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)

      const payload = {
        roomId: parseInt(formData.roomId),
        customerId: parseInt(formData.customerId),
        checkIn: new Date(formData.checkIn).toISOString(),
        checkOut: formData.checkOut ? new Date(formData.checkOut).toISOString() : undefined,
        status: formData.status,
      }

      const response = await PUT(`/hotel-reservation/${id}`, payload)

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Reserva atualizada',
          text: 'Reserva atualizada com sucesso!',
        }).then(() => {
          navigate('/hospitality/hotel-reservation')
        })
      } else {
        throw new Error(response.message || 'Erro ao atualizar reserva')
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e.message || 'Erro ao atualizar a reserva!',
      })
      console.log(e)
    }
  }

  const getSelectedRoomDetails = () => {
    const room = rooms.find((r) => r.id.toString() === formData.roomId)
    return room
  }

  const getSelectedCustomerDetails = () => {
    const customer = customers.find((c) => c.id.toString() === formData.customerId)
    return customer
  }

  const canEditField = (field: string) => {
    if (!originalReservation) return true

    // Regras de negócio para edição baseadas no status
    switch (originalReservation.status) {
      case ReservationRoomStatus.COMPLETED:
      case ReservationRoomStatus.CANCELLED:
        // Reservas finalizadas só permitem alteração de status
        return field === 'status'

      case ReservationRoomStatus.IN_PROGRESS:
        // Em andamento permite alterar check-out e status
        return ['checkOut', 'status'].includes(field)

      default:
        // Outros status permitem editar tudo
        return true
    }
  }

  /*Rota para recuperar as reservas do usuario Atividades - Passeios - Experiências - Serviços */

  const getReservationAtividades = async (id: string) => {
    try {
      const res = await GET(`/reservation-booking/${id}/activities`)
      setAtividades(res.data || [])
    } catch (e) {
      console.log(e)
    }
  }
  const getReservationTours = async (id: string) => {
    try {
      const res = await GET(`/reservation-booking/${id}/tours`)
      setTours(res.data || [])
    } catch (e) {
      console.log(e)
    }
  }
  const getReservationExperiencias = async (id: string) => {
    try {
      const res = await GET(`/reservation-booking/${id}/experiences`)
      setExperiencias(res.data || [])
    } catch (e) {
      console.log(e)
    }
  }

  const getReservationServices = async (id: string) => {
    try {
      const res = await POST(`/order-service-reservation-hotel/filter`, {reservationId: id})
      setReservationServices(res.data || [])
    } catch (e) {
      console.log(e)
    }
  }

  // Função para calcular o resumo das reservas
  const calculateReservationSummary = () => {
    const allReservations = [...atividades, ...tours, ...experiencias]

    const summary: ReservationSummary = {
      totalReservations: allReservations.length + reservationServices.length,
      totalAmount: 0,
      currency: 'BRL',
      byType: {
        activities: {count: atividades.length, total: 0},
        tours: {count: tours.length, total: 0},
        experiences: {count: experiencias.length, total: 0},
        services: {count: reservationServices.length, total: 0},
      },
      byStatus: {},
      totalParticipants: 0,
    }

    // Calcular totais por tipo
    atividades.forEach((activity) => {
      summary.byType.activities.total += parseFloat(activity.totalAmount ?? 0)
      summary.totalAmount += parseFloat(activity.totalAmount ?? 0)
      summary.totalParticipants += activity.participants

      if (!summary.byStatus[activity.status]) {
        summary.byStatus[activity.status] = {count: 0, total: 0}
      }
      summary.byStatus[activity.status].count++
      summary.byStatus[activity.status].total += parseFloat(activity.totalAmount ?? 0)
    })

    tours.forEach((tour) => {
      summary.byType.tours.total += parseFloat(tour.totalAmount ?? 0)
      summary.totalAmount += parseFloat(tour.totalAmount ?? 0)
      summary.totalParticipants += tour.participants

      if (!summary.byStatus[tour.status]) {
        summary.byStatus[tour.status] = {count: 0, total: 0}
      }
      summary.byStatus[tour.status].count++
      summary.byStatus[tour.status].total += parseFloat(tour.totalAmount ?? 0)
    })

    experiencias.forEach((experience) => {
      summary.byType.experiences.total += parseFloat(experience.totalAmount ?? 0)
      summary.totalAmount += parseFloat(experience.totalAmount ?? 0)
      summary.totalParticipants += experience.participants

      if (!summary.byStatus[experience.status]) {
        summary.byStatus[experience.status] = {count: 0, total: 0}
      }
      summary.byStatus[experience.status].count++
      summary.byStatus[experience.status].total += parseFloat(experience.totalAmount ?? 0)
    })

    reservationServices.forEach((service) => {
      summary.byType.services.total += parseFloat(service.price?.toString() ?? 0)
      summary.totalAmount += parseFloat(service.price?.toString() ?? 0)
      summary.totalParticipants += service.peopleQty ?? 0

      if (!summary.byStatus[service.status]) {
        summary.byStatus[service.status] = {count: 0, total: 0}
      }
      summary.byStatus[service.status].count++
      summary.byStatus[service.status].total += parseFloat(service.price?.toString() ?? 0)
    })

    setReservationSummary(summary)
  }

  // Função para calcular o valor total da diária
  const calculateDailyRate = () => {
    if (!formData.checkIn || !selectedRoom) {
      return {
        days: 0,
        totalDaily: 0,
        dailyRate: 0,
        checkInDate: new Date(),
        checkOutDate: new Date(),
      }
    }

    const checkInDate = new Date(formData.checkIn)
    const checkOutDate = formData.checkOut ? new Date(formData.checkOut) : new Date()

    // Calcular diferença em dias
    const timeDiff = checkOutDate.getTime() - checkInDate.getTime()
    const days = Math.ceil(timeDiff / (1000 * 3600 * 24))

    // Garantir que seja pelo menos 1 dia
    const finalDays = Math.max(days, 1)
    const dailyRate = selectedRoom.price
    const totalDaily = finalDays * dailyRate

    return {
      days: finalDays,
      totalDaily,
      dailyRate,
      checkInDate,
      checkOutDate,
    }
  }

  const getOrdersFromStores = async (id: string, stores: string[]) => {
    try {
      setLoadingOrders(true)
      const res = await POST(`/orders/search-by-hotel-context`, {
        pluginspaceIds: stores,
        reservationId: id,
      })
      if (res.success) {
        const ordersData: HotelOrdersResponse = res
        setHotelOrders(ordersData.data || [])
        setHotelOrdersTotal({
          totalSpent: ordersData.totalSpent || 0,
          totalPay: ordersData.totalPay || 0,
        })
      }
    } catch (e) {
      console.log('Erro ao carregar pedidos das lojas:', e)
      setHotelOrders([])
      setHotelOrdersTotal({totalSpent: 0, totalPay: 0})
    } finally {
      setLoadingOrders(false)
    }
  }

  const getHotelStores = async (id: string) => {
    try {
      const res = await GET(`/stores-hotel`)

      if (res.success) {
        const stores = res.data.map((store: any) => store.pluginspace.id)
        await getOrdersFromStores(id, stores)
      }
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    if (id) {
      Promise.all([
        getReservation(),
        getRooms(),
        getCustomers(),
        getReservationAtividades(id),
        getReservationTours(id),
        getReservationExperiencias(id),
        getHotelStores(id),
        getReservationServices(id),
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Effect para recalcular o resumo quando as reservas mudarem
  useEffect(() => {
    if (
      atividades.length > 0 ||
      tours.length > 0 ||
      experiencias.length > 0 ||
      reservationServices.length > 0
    ) {
      calculateReservationSummary()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atividades, tours, experiencias, reservationServices])

  const selectedRoom = getSelectedRoomDetails()
  const selectedCustomer = getSelectedCustomerDetails()

  // Configuração das abas para o HospitalityTabs
  const createTabs = (): TabItem[] => {
    return [
      {
        id: 'basic',
        label: i18n.t('hospitality_room_reservation.tab_basic'),
        icon: 'fas fa-info-circle',
        content: (
          <BasicInfoTab
            formData={formData}
            id={id!}
            originalReservation={originalReservation}
            rooms={rooms}
            customers={customers}
            loading={loading}
            onInputChange={handleInputChange}
            onSave={updateReservation}
            onCancel={() => navigate('/hospitality/hotel-reservation')}
            canEditField={canEditField}
            getSelectedRoomDetails={getSelectedRoomDetails}
            getSelectedCustomerDetails={getSelectedCustomerDetails}
          />
        ),
      },
      {
        id: 'summary',
        label: i18n.t('hospitality_room_reservation.tab_summary'),
        icon: 'fas fa-chart-pie',
        content: (
          <SummaryTab
            formData={formData}
            selectedRoom={selectedRoom}
            selectedCustomer={selectedCustomer}
            reservationSummary={reservationSummary}
            hotelOrders={hotelOrders}
            hotelOrdersTotal={hotelOrdersTotal}
            loadingOrders={loadingOrders}
            calculateDailyRate={calculateDailyRate}
            onNavigateToTab={setActiveTab}
          />
        ),
      },
      {
        id: 'activities',
        label: `${i18n.t('hospitality_room_reservation.tab_activities')}${
          atividades.length > 0 ? ` (${atividades.length})` : ''
        }`,
        icon: 'fas fa-running',
        content: <ActivitiesTab atividades={atividades} />,
      },
      {
        id: 'tours',
        label: `${i18n.t('hospitality_room_reservation.tab_tours')}${
          tours.length > 0 ? ` (${tours.length})` : ''
        }`,
        icon: 'fas fa-map-marked-alt',
        content: <ToursTab tours={tours} />,
      },
      {
        id: 'experiences',
        label: `${i18n.t('hospitality_room_reservation.tab_experiences')}${
          experiencias.length > 0 ? ` (${experiencias.length})` : ''
        }`,
        icon: 'fas fa-star',
        content: <ExperiencesTab experiencias={experiencias} />,
      },
      {
        id: 'services',
        label: `${i18n.t('hospitality_room_reservation.tab_services')}${
          reservationServices.length > 0 ? ` (${reservationServices.length})` : ''
        }`,
        icon: 'fas fa-concierge-bell',
        content: <ServicesTab services={reservationServices} />,
      },
      {
        id: 'orders',
        label: `${i18n.t('hospitality_room_reservation.tab_orders')}${
          hotelOrders.length > 0 ? ` (${hotelOrders.length})` : ''
        }`,
        icon: 'fas fa-shopping-cart',
        content: (
          <HotelOrdersTab
            orders={hotelOrders}
            totalSpent={hotelOrdersTotal.totalSpent}
            totalPay={hotelOrdersTotal.totalPay}
            loading={loadingOrders}
            showList={true}
          />
        ),
      },
    ]
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Editar Reserva de Quarto</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>Editar reserva #{id}</span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t(`buttons.back`)}
                  color='info'
                  iconColor='#FFF'
                  icon='fas fa-arrow-left'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/hotel-reservation')}
                  btnClass='mb-5 fw-bolder me-3'
                />
              </div>
            </div>
            <div className='card-body'>
              <HospitalityTabs
                tabs={createTabs()}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const EditHotelReservation: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: i18n.t('hospitality_room_reservation.breadcrumb_dashboard'),
            path: 'dashboard',
            isActive: false,
          },
          {
            title: i18n.t('hospitality_room_reservation.breadcrumb_reservation'),
            path: 'hotel-reservation',
            isActive: false,
          },
          {
            title: i18n.t('hospitality_room_reservation.breadcrumb_edit'),
            path: 'hotel-reservation/editar',
            isActive: true,
          },
        ]}
      >
        {i18n.t('hospitality_room_reservation.title_edit')}
      </PageTitle>
      <EditHotelReservationContent />
    </>
  )
}

export {EditHotelReservation}
